import { FieldErrors, Resolver, useForm } from "react-hook-form";

import { ReportSectionsType } from "@api/graphql/types-and-hooks";
import { isFieldContentEmpty } from "@components/FieldComponent/utils";
import { buildInvalidDateMessage, isDateFieldContentValid } from "@components/ReportField/inputs/dateFieldUtils";

export type ReportFormValues = Record<string, string>;

const collectFields = (sections: ReportSectionsType[]) =>
  sections
    .flatMap((section) => section?.sectionField?.edges ?? [])
    .map((edge) => edge?.node)
    .filter((node): node is NonNullable<typeof node> => node != null);

export const useReportForm = (sections: ReportSectionsType[]) => {
  const fields = collectFields(sections);

  const values: ReportFormValues = {};
  for (const field of fields) {
    values[field.id] = field.content ?? "";
  }

  const resolver: Resolver<ReportFormValues> = (formValues) => {
    const errors: FieldErrors<ReportFormValues> = {};
    for (const field of fields) {
      const content = formValues[field.id] ?? values[field.id];
      if (field.templateSectionField?.isRequired && isFieldContentEmpty(field.fieldType, content)) {
        errors[field.id] = { type: "required", message: `${field.title || "Field"} is required.` };
      } else if (!isDateFieldContentValid(field.fieldType, field.fieldIdentifier, content)) {
        errors[field.id] = { type: "invalidDate", message: buildInvalidDateMessage(content) };
      }
    }
    if (Object.keys(errors).length) {
      return { values: {}, errors };
    }
    return { values: formValues, errors: {} };
  };

  return useForm<ReportFormValues>({
    values,
    resolver,
    // A field save re-queries the report, changing `values` and re-syncing the
    // form; keep validation state so saving one field doesn't clear the others'
    // visible errors (the fixed field still clears its own via onChange).
    resetOptions: { keepDirtyValues: true, keepErrors: true, keepIsSubmitted: true, keepTouched: true },
    shouldFocusError: false,
  });
};
